import type { ICellCommentThread } from "./types";

const cellKey = (row: number, col: number) => `${row}:${col}`;

export interface CellCommentIndex {
  // The threads on one saved (1-based) cell, open ones first.
  threadsAt: (row: number, col: number) => ICellCommentThread[];
  // Whether the cell carries at least one open thread — drives the marker.
  hasOpenThreadAt: (row: number, col: number) => boolean;
}

/** Indexes the threads placed on one sheet by their saved coordinate. */
export const buildCellCommentIndex = (
  threads: ICellCommentThread[] | undefined,
  sheetId: string | null,
): CellCommentIndex => {
  const byCell = new Map<string, ICellCommentThread[]>();

  threads?.forEach((thread) => {
    const { sheet_id, row, col } = thread.placement;
    if (!sheetId || sheet_id !== sheetId || row == null || col == null) return;

    const key = cellKey(row, col);
    byCell.set(key, [...(byCell.get(key) ?? []), thread]);
  });
  byCell.forEach((cellThreads) =>
    cellThreads.sort(
      (left, right) =>
        Number(!!left.resolved_at) - Number(!!right.resolved_at) ||
        left.created_at.localeCompare(right.created_at),
    ),
  );

  return {
    threadsAt: (row, col) => byCell.get(cellKey(row, col)) ?? [],
    hasOpenThreadAt: (row, col) =>
      (byCell.get(cellKey(row, col)) ?? []).some(
        (thread) => !thread.resolved_at,
      ),
  };
};
